import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { reportsApi } from "@/services/api";
import type { ReportCreate } from "@/types";

export function useReportList(params?: { skip?: number; limit?: number }) {
  return useQuery({
    queryKey: ["reports", params],
    queryFn: async () => {
      const response = await reportsApi.list(params);
      return response.data;
    },
  });
}

export function useReportDetail(id: number | undefined) {
  return useQuery({
    queryKey: ["reports", "detail", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await reportsApi.get(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ReportCreate) => {
      const response = await reportsApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await reportsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useDownloadReport() {
  const download = useCallback(async (id: number, fileName?: string) => {
    const response = await reportsApi.download(id);
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || `report-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  return { download };
}

export function useDownloadAnalysisPptx() {
  const download = useCallback(
    async (analysisId: number, fileName?: string) => {
      const response = await reportsApi.downloadAnalysisPptx(analysisId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || `analysis-${analysisId}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [],
  );

  return { download };
}
